/**
 * Per-category cover gradient for groups with no photo. Deterministic — the
 * same category always renders the same color, so the list doesn't reshuffle
 * on refetch — and it gives the no-image fallback meaning (you recognise a
 * group by its color) instead of the old dead gray-brown.
 *
 * Tuned as MID-TONE vibrants (≈ the 500→700 range) on purpose so they play
 * nice in BOTH themes: vivid on the white light-mode card, rich-but-not-
 * glaring on the dark-mode gray-900 card, and dark enough that the white
 * category-icon watermark + the translucent badge/heart overlays keep their
 * contrast either way. The text below the cover sits on the theme-aware card
 * background, which already adapts.
 *
 * Shared by the mobile group list card, group-detail header and cover-image
 * placeholder, and by the web landing page's use-case badges, so every
 * no-photo surface matches.
 */
export const CATEGORY_GRADIENTS: Record<string, readonly [string, string]> = {
  trip: ['#0ea5e9', '#0369a1'], // sky — sea / travel
  friends: ['#8b5cf6', '#6d28d9'], // violet — social
  couple: ['#f43f5e', '#be123c'], // rose — romantic
  family: ['#10b981', '#047857'], // emerald — home / warmth
  roommates: ['#14b8a6', '#0f766e'], // teal — shared space
  events: ['#f97316', '#c2410c'], // orange — the SplitFiesta brand; a fiesta
  work: ['#6366f1', '#4338ca'], // indigo — professional
  other: ['#64748b', '#334155'], // slate — neutral but alive
};

/**
 * Resolve a group category to its cover gradient `[from, to]` (diagonal).
 * Falls back to the neutral `other` slate for unknown / missing categories.
 */
export function getGroupCategoryGradient(
  category: string | undefined
): readonly [string, string] {
  return CATEGORY_GRADIENTS[category ?? 'other'] ?? CATEGORY_GRADIENTS.other;
}
